/**
 * Shows the copy that belongs to the shade, and the cartridge, the shopper has picked.
 *
 * Every candidate is rendered by Liquid and all but the matching one carry `hidden`,
 * which is the DOM filter the swatch grids already use. So the right copy is in the
 * page before this module runs, and a page with no shades on it costs nothing.
 *
 * Each toggled node carries `data-kb-shade-copy-item` plus whichever keys apply:
 * `data-shade-name` for the shade, and `data-cartridge-id` for the cartridge the shade
 * belongs to, which is what the pen page needs because its shades sit on the cartridges.
 * A node with neither key is always shown, which is how a cartridge page's shared
 * ingredients list rides along.
 */
class KbPdpShadeCopy extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      host: 'kb-pdp-product',
      item: '[data-kb-shade-copy-item]',
    };

    this.eventNames = {
      variantChange: 'kb:variant:change',
    };

    /** @type {AbortController|null} */
    this.abortController = null;

    /** @type {HTMLElement|null} */
    this.productHost = null;
  }

  connectedCallback() {
    const host = this.closest(this.selectors.host);
    if (!(host instanceof HTMLElement)) return;

    this.productHost = host;

    // Bound here rather than in the constructor: being re-parented must not leave the
    // element inert, which an abort in the constructor's lifetime would do.
    this.abortController = new AbortController();
    host.addEventListener(this.eventNames.variantChange, this.handleVariantChange, {
      signal: this.abortController.signal,
    });

    const selection = /** @type {{ selection?: object|null }} */ (host).selection;
    if (selection) this.paint(selection);
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
    this.productHost = null;
  }

  /** @param {Event} event */
  handleVariantChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail) this.paint(detail);
  };

  /** @param {object} selection */
  paint(selection) {
    const items = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.item));
    items.forEach((item) => {
      item.hidden = !this.itemMatchesSelection(item, selection);
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {object} selection
   * @returns {boolean}
   */
  itemMatchesSelection(item, selection) {
    const state = /** @type {{ cartridgeProductId?: unknown, shadeName?: unknown }} */ (selection);

    const cartridgeId = item.dataset.cartridgeId;
    if (cartridgeId && !this.matchesId(cartridgeId, state.cartridgeProductId)) return false;

    const shadeName = item.dataset.shadeName;
    if (shadeName && shadeName !== state.shadeName) return false;

    return true;
  }

  /**
   * @param {unknown} left
   * @param {unknown} right
   * @returns {boolean}
   */
  matchesId(left, right) {
    if (left == null || right == null) return false;
    return String(left) === String(right);
  }
}

if (!customElements.get('kb-pdp-shade-copy')) {
  customElements.define('kb-pdp-shade-copy', KbPdpShadeCopy);
}
